import { useAppTranslation } from "@/utils/useAppTranslation";
import { Close } from "@payping/icons";
import {
  Button,
  Card,
  Checkbox,
  Col,
  Drawer,
  Flex,
  Row,
  Spin,
  Typography,
  useMessagedAsync,
  useMessagedAsyncFn,
} from "@payping/web-components";
import { FC, useState } from "react";
import UserSignContractCreateSign from "./create-sign";
import UserSignContractContent from "./content";
import { useAppDispatch } from "@/utils";
import {
  getCurrentUserDetail,
  getPersonalInfo,
  getUserBusinessinfo,
  getUserLocalInformation,
} from "@payping/redux/actions";
import { fileApis, FileTypeEnum, planApis, uploadApis, userApis, WageTypeEnum } from "@payping/api";
import { dayjs } from "@payping/utilities-shared/date";
import { buildUrl } from "@payping/utilities-shared/url";
import { URLS } from "@payping/utilities-shared/consts";

const STEPS = {
  CONTRACT: "CONTRACT",
  SIGN_CONTRACT: "SIGN_CONTRACT",
} as const;

type Step = (typeof STEPS)[keyof typeof STEPS];

export const UserSignContract: FC = () => {
  const { t } = useAppTranslation();

  const [openDrawer, setOpenDrawer] = useState(false);
  const [currentStep, setCurrentStep] = useState<Step>(STEPS.CONTRACT);
  const [userISAgreeWithContract, setUserIsAgreeWithContract] = useState(false);

  const dispatch = useAppDispatch();

  const getUserState = useMessagedAsync(
    async () => {
      const userDetails = await dispatch(getCurrentUserDetail());
      const businessCategory = await userApis.businessCategoryWithCancellationTokenGet();
      const personalInfo = await dispatch(getPersonalInfo());
      const localInfo = await dispatch(getUserLocalInformation());

      const userExtra = await userApis.extraInfoWithCancellationTokenGet();
      const userBusiness = await dispatch(getUserBusinessinfo());

      const shebaInfo = await userApis.bankWithCancellationTokenGet();

      const allResponses = {
        userDetails,
        businessCategory,
        personalInfo,
        localInfo,
        userBusiness,
        shebaInfo,
        userExtra,
      } as const;

      if (userDetails.currentPlanId !== null && userDetails.currentPlanId !== undefined) {
        const currentPlan = await planApis.getEnabledProfilePlanWithPlanIdWithCancellationTokenGet(
          userDetails.currentPlanId,
        );

        return {
          ...allResponses,
          currentPlan,
        };
      }

      return { ...allResponses, currentPlan: undefined };
    },

    [],
    {
      success: false,
      loading: false,
    },
  );

  const [signContractState, signContract] = useMessagedAsyncFn(async (file: File) => {
    const fileUrl = await uploadApis.profilePicWithFilePost(file);
    await fileApis.addFileWithRequestModelWithCancellationTokenPost({
      fileName: fileUrl,
      type: FileTypeEnum.Signature,
    });
    await userApis.signContractWithRequestModelWithCancellationTokenPost({ accept: true });

    handleCloseDrawer();
  }, []);

  const handleOpenDrawer = () => {
    setOpenDrawer(true);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
  };

  if (getUserState.loading) {
    return (
      <Card.Ant className="tw-w-full">
        <Flex className="tw-w-full" justify="center">
          <Spin />
        </Flex>
      </Card.Ant>
    );
  }

  if (!!getUserState.error || getUserState.value?.userDetails.isSettlement) return null;

  return (
    <>
      <Card.Ant className="tw-w-full">
        <Flex align="start" gap={"middle"} className="tw-w-full">
          <div className="tw-max-h-[80px] tw-max-w-[80px]">
            <img
              src={buildUrl(URLS.STORAGE_URL, "dashboard-app/new-user-sign-contract.png").toString()}
              className="tw-h-full tw-w-full tw-object-fill"
            />
          </div>
          <Flex vertical>
            <Typography.Title level={5}>{t("overview.userSignContract.title")}</Typography.Title>
            <Typography.Paragraph type="secondary">{t("overview.userSignContract.description")}</Typography.Paragraph>
            <div>
              <Button onClick={handleOpenDrawer} color="primary" variant="solid" block={false}>
                {t("overview.userSignContract.action")}
              </Button>
            </div>
          </Flex>
        </Flex>
      </Card.Ant>
      {openDrawer ? (
        <Drawer
          title={t("overview.userSignContract.modal.title")}
          height={"100%"}
          width={"100%"}
          placement="bottom"
          open={openDrawer}
          onClose={handleCloseDrawer}
          closeIcon={false}
          extra={<Button type={"text"} icon={<Close width={14} height={14} />} onClick={handleCloseDrawer} />}
        >
          <Row>
            <Col lg={6}></Col>
            <Col lg={12}>
              {currentStep === STEPS.CONTRACT ? (
                <Flex vertical className="tw-w-full" gap={"large"}>
                  <Typography.Title level={5} className="!tw-mb-0 tw-text-center">
                    {t("overview.userSignContract.modal.steps.contract.title")}
                  </Typography.Title>
                  <Flex vertical className="tw-relative tw-h-[50dvh] tw-overflow-auto lg:tw-h-[60dvh]">
                    <UserSignContractContent
                      customerFullName={`${getUserState?.value?.personalInfo?.firstName ?? ""} ${getUserState?.value?.personalInfo?.lastName ?? ""}`}
                      customerFatherName={getUserState?.value?.personalInfo?.fatherName ?? ""}
                      customerNationalCode={getUserState?.value?.personalInfo?.nationalCode ?? ""}
                      customerAddress={getUserState?.value?.localInfo?.address ?? ""}
                      customerWebsite={getUserState?.value?.userBusiness?.site ?? ""}
                      customerStartContract={dayjs(new Date()).calendar("jalali").format("YYYY/MM/DD")}
                      customerEndContract={dayjs().calendar("jalali").add(1, "year").format("YYYY/MM/DD")}
                      customerCategoryName={getUserState?.value?.businessCategory?.categoryName ?? ""}
                      customerPlanPrice={getUserState?.value?.currentPlan?.discountedPrice?.toString() ?? ""}
                      customerPlanWagePercent={
                        getUserState.value?.currentPlan?.wageType === WageTypeEnum.Percentage
                          ? ((getUserState.value?.currentPlan?.wageValue ?? 0 * 10)?.toString() ?? "0")
                          : "100"
                      }
                      customerPlaneWageAmount={
                        getUserState.value?.currentPlan?.wageType === WageTypeEnum.Fixed
                          ? (getUserState.value?.currentPlan?.wageValue ?? 0 * 10)?.toString()
                          : (getUserState.value?.currentPlan?.maximumWage?.toString() ?? "0")
                      }
                      customerShebaNumber={getUserState.value?.shebaInfo.shaba ?? ""}
                      customerIsBussines={getUserState.value?.userExtra.businessInfo?.isOfficialSubmited ?? false}
                      customerCompanyName={getUserState.value?.userExtra.businessInfo?.companyName ?? ""}
                    />
                  </Flex>
                  <Checkbox
                    checked={userISAgreeWithContract}
                    onChange={() => setUserIsAgreeWithContract((pre) => !pre)}
                  >
                    {t("overview.userSignContract.modal.steps.contract.confirmation")}
                  </Checkbox>
                  <div>
                    <Button
                      color="primary"
                      variant="solid"
                      disabled={!userISAgreeWithContract}
                      onClick={() => {
                        setCurrentStep(STEPS.SIGN_CONTRACT);
                      }}
                    >
                      {t("overview.userSignContract.modal.steps.contract.submit")}
                    </Button>
                  </div>
                </Flex>
              ) : (
                <UserSignContractCreateSign
                  onFinish={async (file) => {
                    await signContract(file);
                    handleCloseDrawer();
                  }}
                  loading={signContractState.loading}
                />
              )}
            </Col>
            <Col lg={6}></Col>
          </Row>
        </Drawer>
      ) : null}
    </>
  );
};
